/**
 * Products Store (Pinia)
 * Manages product list, product details, and product-related state
 * Implements fallback strategy: API first, then Mock data
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as productsApi from '@/api/products'
import { adaptProductsFromApi, adaptProductFromApi } from '@/utils/dataAdapter'
import { products as mockProducts } from '@/data/products'

export const useProductsStore = defineStore('products', () => {
  // State
  const products = ref([])
  const currentProduct = ref(null)
  const pagination = ref({})
  const loading = ref(false)
  const error = ref(null)
  const usingMockData = ref(false)  // Flag to indicate if using mock data due to API failure

  // Computed properties
  const hasError = computed(() => !!error.value)

  /**
   * Fetch products list with parameters
   * Implements fallback strategy: try API first, then fallback to Mock
   * @param {Object} params - Query parameters (page, pageSize, application_field_id, search)
   * @returns {Promise<Object>} Response data
   */
  async function fetchProducts(params = {}) {
    loading.value = true
    error.value = null

    try {
      // Try API first
      const result = await productsApi.getProducts(params)

      if (result.code === 1 && result.data) {
        // API success
        const adaptedProducts = adaptProductsFromApi(result.data.list || [])
        products.value = adaptedProducts
        pagination.value = result.data.pagination || {}
        usingMockData.value = false

        return { success: true, data: result.data }
      } else {
        // API returned error code, fallback to Mock
        useMockData()
        return { success: false, data: null }
      }
    } catch (err) {
      // Network error, fallback to Mock
      console.error('Error fetching products from API:', err)
      useMockData()
      return { success: false, data: null }
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch product detail by ID
   * Implements fallback strategy: try API first, then fallback to Mock
   * @param {number|string} id - Product ID
   * @returns {Promise<Object|null>} Product object or null if not found
   */
  async function fetchProductById(id) {
    loading.value = true
    error.value = null

    try {
      // Try API first
      const result = await productsApi.getProductById(id)

      if (result.code === 1 && result.data) {
        // API success
        const adaptedProduct = adaptProductFromApi(result.data)
        currentProduct.value = adaptedProduct
        usingMockData.value = false

        return adaptedProduct
      } else {
        // API returned error code, fallback to Mock
        const mockProduct = mockProducts.find(p => p.id.toString() === id.toString())
        if (mockProduct) {
          currentProduct.value = mockProduct
          usingMockData.value = true
          return mockProduct
        } else {
          error.value = '产品不存在'
          currentProduct.value = null
          return null
        }
      }
    } catch (err) {
      // Network error, fallback to Mock
      console.error('Error fetching product from API:', err)

      const mockProduct = mockProducts.find(p => p.id.toString() === id.toString())
      if (mockProduct) {
        currentProduct.value = mockProduct
        usingMockData.value = true
        return mockProduct
      } else {
        error.value = '产品不存在'
        currentProduct.value = null
        return null
      }
    } finally {
      loading.value = false
    }
  }

  /**
   * Use Mock data (fallback strategy)
   * Loads all mock products and sets the flag
   */
  function useMockData() {
    products.value = mockProducts
    pagination.value = {
      page: 1,
      pageSize: mockProducts.length,
      total: mockProducts.length,
      totalPages: 1
    }
    usingMockData.value = true
  }

  /**
   * Set products list (direct set)
   * @param {Array} newProducts - New products array
   */
  function setProducts(newProducts) {
    products.value = newProducts
  }

  /**
   * Set current product (direct set)
   * @param {Object} product - Product object
   */
  function setCurrentProduct(product) {
    currentProduct.value = product
  }

  /**
   * Clear current product
   */
  function clearCurrentProduct() {
    currentProduct.value = null
  }

  /**
   * Clear products list
   */
  function clearProducts() {
    products.value = []
    pagination.value = {}
  }

  /**
   * Clear error
   */
  function clearError() {
    error.value = null
  }

  return {
    // State
    products,
    currentProduct,
    pagination,
    loading,
    error,
    usingMockData,

    // Computed
    hasError,

    // Actions
    fetchProducts,
    fetchProductById,
    useMockData,
    setProducts,
    setCurrentProduct,
    clearCurrentProduct,
    clearProducts,
    clearError
  }
})
